import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { hasCapability } from '@/lib/roles';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Loader2, Search } from 'lucide-react';
import IndicatorRow from '@/components/intake/IndicatorRow';
import NewRequestButton from '@/components/intake/NewRequestButton';
import DownloadCsvButton from '@/components/intake/DownloadCsvButton';
import RequestDrawer from '@/components/intake/RequestDrawer';
import {
  APPROVAL_TONE, DEFAULT_STATE_TONE, INTAKE_SOURCES, STATE_LABEL, STATE_TONE, STATUS_GROUP_LABEL,
  SUBMITTER_ACTION_STATES, SUBMITTER_ROW_HINT, formatDate, g1Text, sourceLabel, stateLabel,
} from '@/lib/intakeReview';

// The intake register: one page for every role, scoped by the backend.
// "All requests" (holders of intake:read_all: admin, manager, pm,
// intake_reviewer) reads /intake-review/requests; "Submitted by me" (every
// role, the only tab for requester and viewer) reads /intake-review/mine.
// Search and filters run on the server, so the page and the total reflect
// them. A row opens the request in the Drawer at /intake-requests/:itemId.

const PAGE_SIZE = 25;
const DATE_RANGES = [
  { value: '', label: 'Any date' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last 12 months' },
];

const selectClass = 'h-10 px-3 border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-[#1a1a2e] text-sm text-[#30302F] dark:text-white';
const tabClass =
  'data-[state=active]:bg-[#98139C] data-[state=active]:text-white ' +
  'data-[state=active]:shadow-sm dark:data-[state=active]:bg-[#98139C] ' +
  'text-[#30302F] dark:text-white/80 motion-reduce:transition-none';

const endpointFor = (t) => (t === 'all' ? '/intake-review/requests' : '/intake-review/mine');

function daysAgo(days) {
  const d = new Date(Date.now() - Number(days) * 24 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
}

export default function IntakeRequests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { itemId } = useParams();
  const [searchParams] = useSearchParams();
  const canAll = hasCapability(user, 'readAllIntake');
  const tab = canAll && searchParams.get('tab') !== 'mine' ? 'all' : 'mine';

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [tabTotals, setTabTotals] = useState({});
  const [businessUnits, setBusinessUnits] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [refresh, setRefresh] = useState(0);

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [businessUnit, setBusinessUnit] = useState('');
  const [dateRange, setDateRange] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // A new tab or filter starts at the first page.
  useEffect(() => { setPage(0); }, [tab, debounced, status, source, businessUnit, dateRange]);

  // The filters as query parameters; the list and the CSV export share them.
  const filterParams = useMemo(() => {
    const params = {};
    if (debounced) params.q = debounced;
    if (status) params.state = status;
    if (source) params.source = source;
    if (businessUnit) params.business_unit = businessUnit;
    if (dateRange) params.submitted_from = daysAgo(dateRange);
    return params;
  }, [debounced, status, source, businessUnit, dateRange]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const params = { ...filterParams, limit: PAGE_SIZE, skip: page * PAGE_SIZE };
    (async () => {
      try {
        const res = await api.get(endpointFor(tab), { params });
        if (!active) return;
        setRows(res.data?.items || []);
        setTotal(res.data?.total || 0);
        setBusinessUnits(res.data?.business_units || []);
      } catch (err) {
        if (!active) return;
        setRows([]);
        setTotal(0);
        setError(err?.response?.status === 403
          ? 'You do not have access to these requests.'
          : 'Could not load intake requests.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [tab, page, filterParams, refresh]);

  // Tab totals (unfiltered) and the four counts for the caller's scope.
  useEffect(() => {
    let active = true;
    const tabs = canAll ? ['all', 'mine'] : ['mine'];
    (async () => {
      const totals = {};
      await Promise.all(tabs.map(async (t) => {
        try {
          const res = await api.get(endpointFor(t), { params: { limit: 1, skip: 0 } });
          totals[t] = res.data?.total ?? 0;
        } catch {
          totals[t] = undefined;
        }
      }));
      if (active) setTabTotals(totals);
    })();
    (async () => {
      try {
        const res = await api.get(canAll ? '/intake-review/indicators' : '/intake-review/indicators/mine');
        if (active) setCounts(res.data);
      } catch {
        if (active) setCounts(null);
      }
    })();
    return () => { active = false; };
  }, [canAll, refresh]);

  const tabQuery = tab === 'mine' ? '?tab=mine' : '';
  const openRow = (id) => navigate(`/intake-requests/${encodeURIComponent(id)}${tabQuery}`);
  const closeDrawer = () => navigate(`/intake-requests${tabQuery}`);
  const onDecided = useCallback(() => setRefresh((n) => n + 1), []);
  const setTab = (t) => navigate(t === 'mine' ? '/intake-requests?tab=mine' : '/intake-requests');

  const first = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const last = Math.min(total, (page + 1) * PAGE_SIZE);
  const tabLabel = (t, text) => (tabTotals[t] === undefined ? text : `${text} (${tabTotals[t]})`);

  return (
    <div className="p-4 lg:p-8 space-y-8" data-testid="intake-requests-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#98139C]">Intake</p>
          <h1 className="text-4xl font-heading text-[#30302F] dark:text-white mt-1" style={{ letterSpacing: '-0.8px' }}>
            Intake requests
          </h1>
          <p className="text-[#30302F] dark:text-white mt-2 font-light text-lg">
            {canAll ? 'Every intake request, and the ones you submitted.' : 'The requests you submitted and where they stand.'}
          </p>
        </div>
        <div className="flex items-start gap-3">
          <DownloadCsvButton endpoint={endpointFor(tab)} params={filterParams} />
          <NewRequestButton user={user} />
        </div>
      </div>

      <IndicatorRow counts={counts} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 h-10 p-1">
          {canAll && (
            <TabsTrigger value="all" className={tabClass} data-testid="intake-tab-all">
              {tabLabel('all', 'All requests')}
            </TabsTrigger>
          )}
          <TabsTrigger value="mine" className={tabClass} data-testid="intake-tab-mine">
            {tabLabel('mine', 'Submitted by me')}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#30302F]/60 dark:text-white/60" />
          <Input
            placeholder="Search ID, title or contact"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 w-72 rounded-none border-[#e3e3e3] dark:border-white/10"
            aria-label="Search intake requests"
            data-testid="intake-search"
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectClass} aria-label="Filter by status" data-testid="intake-filter-status">
          <option value="">All statuses</option>
          {Object.entries(STATUS_GROUP_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          {Object.entries(STATE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={source} onChange={(e) => setSource(e.target.value)} className={selectClass} aria-label="Filter by source" data-testid="intake-filter-source">
          <option value="">All sources</option>
          {INTAKE_SOURCES.map((v) => <option key={v} value={v}>{sourceLabel(v)}</option>)}
        </select>
        <select value={businessUnit} onChange={(e) => setBusinessUnit(e.target.value)} className={selectClass} aria-label="Filter by business unit" data-testid="intake-filter-business-unit">
          <option value="">All business units</option>
          {businessUnits.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className={selectClass} aria-label="Filter by submitted date" data-testid="intake-filter-date">
          {DATE_RANGES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-900 dark:text-red-200" role="alert">
          {error}
        </div>
      )}

      {!error && (
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 overflow-x-auto">
          <Table data-testid="intake-table">
            <TableHeader>
              <TableRow>
                <TableHead className="px-4 whitespace-nowrap">ID</TableHead>
                <TableHead className="px-4">Title</TableHead>
                <TableHead className="px-4">Status</TableHead>
                <TableHead className="px-4">Source</TableHead>
                <TableHead className="px-4 whitespace-nowrap">Contact person</TableHead>
                <TableHead className="px-4 whitespace-nowrap">Business unit</TableHead>
                <TableHead className="px-4">Approval</TableHead>
                <TableHead className="px-4">Submitted</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="px-4 py-10 text-center text-[#30302F]/60 dark:text-white/50">
                    <Loader2 size={20} className="animate-spin inline" aria-label="Loading" />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="px-4 py-10 text-center text-[#30302F]/60 dark:text-white/50">
                    No intake requests match.
                  </TableCell>
                </TableRow>
              ) : rows.map((r) => (
                <TableRow
                  key={r.id}
                  className="cursor-pointer"
                  onClick={() => openRow(r.id)}
                  data-testid={`intake-row-${r.id}`}
                >
                  <TableCell className="px-4 whitespace-nowrap font-mono text-xs text-[#30302F] dark:text-white">
                    {r.intake_ref || '-'}
                  </TableCell>
                  <TableCell className="px-4 max-w-md">
                    <Link
                      to={`/intake-requests/${encodeURIComponent(r.id)}${tabQuery}`}
                      onClick={(e) => e.stopPropagation()}
                      className="font-medium text-[#30302F] dark:text-white hover:text-[#98139C] dark:hover:text-[#d87bdb] line-clamp-2"
                    >
                      {r.title || 'Untitled'}
                    </Link>
                    {tab === 'mine' && SUBMITTER_ACTION_STATES.includes(r.state) && (
                      <div className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">{SUBMITTER_ROW_HINT}</div>
                    )}
                  </TableCell>
                  <TableCell className="px-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${STATE_TONE[r.state] || DEFAULT_STATE_TONE}`}>
                      {stateLabel(r.state)}
                    </span>
                  </TableCell>
                  <TableCell className="px-4 whitespace-nowrap text-[#30302F] dark:text-white">{sourceLabel(r.source)}</TableCell>
                  <TableCell className="px-4 text-[#30302F] dark:text-white">
                    {r.contact_name || r.requester?.name || r.requester?.email || '-'}
                  </TableCell>
                  <TableCell className="px-4 text-[#30302F] dark:text-white">{r.business_unit || '-'}</TableCell>
                  <TableCell className="px-4">
                    {r.g1_status ? (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${APPROVAL_TONE[r.g1_status] || ''}`}>
                        {g1Text(r.g1_status)}
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="px-4 whitespace-nowrap text-[#30302F]/80 dark:text-white/80">{formatDate(r.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[#e3e3e3] dark:border-white/10 text-sm text-[#30302F]/80 dark:text-white/70">
            <span data-testid="intake-count">{first} to {last} of {total}</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                className="px-4 h-9 border border-[#e3e3e3] dark:border-white/10 hover:border-[#98139C] disabled:opacity-50 disabled:hover:border-[#e3e3e3]"
                data-testid="intake-prev"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={last >= total || loading}
                className="px-4 h-9 border border-[#e3e3e3] dark:border-white/10 hover:border-[#98139C] disabled:opacity-50 disabled:hover:border-[#e3e3e3]"
                data-testid="intake-next"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      <RequestDrawer itemId={itemId} scope={tab} onClose={closeDrawer} onDecided={onDecided} />
    </div>
  );
}
